<template>
    <CSmartTable
        :id="id"
        :columns="fields"
        :items="items"
        :table-props="tableProps"
        :column-sorter="sorter"
        :sorter-value="sorterValue"
        :items-per-page="500"
        v-bind="$attrs"
        @sorter-change="updateSorterValue"
    >
        <template
            v-for="(_, slot) of $slots"
            #[slot]="scope"
        >
            <slot
                v-if="shouldForwardSlot(slot)"
                :name="slot"
                v-bind="scope"
            />
        </template>
    </CSmartTable>
    <span
        v-for="dynamicComponent in Object.keys(slotsForManualRender)"
        :key="dynamicComponent"
    >
        <component
            :is="slotsForManualRender[dynamicComponent]"
            v-if="tableReady"
        />
    </span>
</template>

<script setup>
import { CSmartTable } from '@coreui/vue-pro'
import { computed, onMounted, h, useSlots, Teleport, ref } from 'vue'

// Props
const props = defineProps({
    fields: {
        type: Array,
        default: () => []
    },
    items: {
        type: Array,
        default: () => []
    },
    hover: {
        type: Boolean,
        default: false
    },
    sorter: {
        type: [Boolean, Object],
        default: false
    },
    sorterValue: {
        type: Object,
        default: () => {}
    },
    id: {
        type: String,
        default: null
    }
})

// Emits
const emit = defineEmits(['update:sorter-value'])

const updateSorterValue = (sorterValue) => {
    emit('update:sorter-value', {
        column: sorterValue.column,
        asc: sorterValue.state === 'asc'
    })
}

const items = computed(() => {
    if (!props.items.some(item => item._classes)) {
        return props.items
    }

    return props.items.map(item => item._classes
        ? { ...item, _props: { class: item._classes } }
        : item
    )
})
const tableProps = computed(() => {
    return {
        hover: props.hover
    }
})
const sorterValue = computed(() => {
    if (props.sorterValue === undefined) {
        return null
    }

    return {
        column: props.sorterValue.column,
        state: props.sorterValue.asc ? 'asc' : 'desc'
    }
})

// Slots
// Indicates whether we should forward the slot to CSMartTable
const shouldForwardSlot = (name) => {
    return name.endsWith('header') === false && name.endsWith('filter') === false
}

// Returns field name from slot name, example: firstname-header => firstname
const getFieldNameFromSlotName = (name) => {
    return name.replace('-header', '').replace('-filter', '')
}

// Returns nth-child for which tr we should use. 1 is header, 2 is for filters
const getTrNth = (name) => {
    return name.endsWith('header') === true
        ? 1 // Header is always first tr
        : 2 // Filters are second tr
}

// Returns nth-child for which td / th we should use. That depends on order of the filed
// Example: if fields are "firstname,lastname" then firstname would be 1, lastname 2
const getCellNth = (name) => {
    name = getFieldNameFromSlotName(name)

    for (const key in props.fields) {
        if (props.fields[key].key === name) {
            return Number(key) + 1
        }
    }

    return 1
}

// Indicates whether the field actually exists for the found slot
const fieldExists = (name) => {
    name = getFieldNameFromSlotName(name)

    for (const field of props.fields) {
        if (field.key === name) {
            return true
        }
    }

    return false
}

// Generate a value for Teleport "to" prop value
// This is done based on specific tr (header or filter) and th / td (header / filter)
const getTeleportTo = (name) => {
    return `${props.id ? `#${props.id}` : ''} table tr:nth-child(${getTrNth(name)}) th:nth-child(${getCellNth(name)})`
}

// Get all passed slots
const slots = useSlots()

// Filter only the ones which shouldn't be forwarded
const slotsToManuallyRender = Object.keys(slots).filter((name) => shouldForwardSlot(name) === false)

// Holds all DOM places which we should manually clean out
// This is needed in order to overwrite default table header and filter
const domPlacesToClear = []

// Holds all slots we should manually render where key is the field name and value is the functional component
const slotsForManualRender = {}

for (const name of slotsToManuallyRender) {
    if (fieldExists(name) === false) {
        continue
    }

    const domTargetPlace = getTeleportTo(name)

    domPlacesToClear.push(domTargetPlace)
    slotsForManualRender[getFieldNameFromSlotName(name)] = () => {
        return h(
            Teleport,
            {
                to: domTargetPlace
            },
            {
                default: () => slots[name]() // Will give the actual contents which was passed through the slot
            }
        )
    }
}

// We need to track when the component is mounted because we can render slots manually only after the table is mounted
const tableReady = ref(false)

onMounted(() => {
    for (const domPlaceToClear of domPlacesToClear) {
        document.querySelectorAll(domPlaceToClear).forEach(function (th) {
            th.innerHTML = ''
        })
    }

    tableReady.value = true
})
</script>

<style>
.table td {
    vertical-align: middle;
}
</style>

<style lang="scss">
.table-sticky-header {
    max-height: 70vh;
    overflow-y: auto;
}

.table-sticky-header table {
    border-collapse: separate;
    border-spacing: 0;
}

.table-sticky-header table thead th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
    border-bottom: 1px solid #ccc;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
}
</style>
